import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useCountry } from '../../components/CountrySelector';
import { useOrderSummary } from '../useOrderSummary';
import {
  UPDATE_ORDER_MUTATION,
  UpdateOrderMutationVariables,
  UpdateOrderMutation,
  UpdateOrderAction,
  OrderState
} from './useUpdateOrder.rest';

export const useUpdateOrder = (orderId: string) => {
  const {
    country: { alpha3Code }
  } = useCountry();
  const { orderNumber = '' } = useParams<{ orderNumber?: string }>();

  const [mutate, { loading, error }] = useMutation<
    UpdateOrderMutation,
    UpdateOrderMutationVariables
  >(UPDATE_ORDER_MUTATION);

  const { orderSummary } = useOrderSummary(orderNumber);

  const setOrderStateToDefault = useCallback(
    () =>
      mutate({
        variables: {
          orderId: orderId,
          input: {
            data: {
              action_type: UpdateOrderAction.UpdatePayment,
              id: orderId,
              type: 'order_summary',
              attributes: {
                country_code: alpha3Code,
                order_state: OrderState.Default
              }
            }
          }
        }
      }),
    [alpha3Code, mutate, orderId]
  );

  const setOrderStateToPaying = useCallback(
    () =>
      mutate({
        variables: {
          orderId: orderId,
          input: {
            data: {
              action_type: UpdateOrderAction.UpdatePayment,
              id: orderId,
              type: 'order_summary',
              attributes: {
                country_code: alpha3Code,
                order_state: OrderState.PayRemainingBalance
              }
            }
          }
        }
      }),
    [alpha3Code, mutate, orderId]
  );

  const setOrderPayRemainingBalance = useCallback(
    (newPaymentSource: string) => {
      mutate({
        variables: {
          orderId: orderId,
          input: {
            data: {
              action_type: UpdateOrderAction.UpdatePayment,
              id: orderId,
              type: 'order_summary',
              attributes: {
                country_code: alpha3Code,
                order_state: OrderState.PayRemainingBalance,
                payment_type: newPaymentSource
              }
            }
          }
        }
      });
    },
    [alpha3Code, mutate, orderId]
  );

  const updateOrderSelectedItems = useCallback(
    (selectedItemsIds: number[]) =>
      mutate({
        variables: {
          orderId: orderId,
          input: {
            data: {
              action_type: UpdateOrderAction.UpdatePayment,
              id: orderId,
              type: 'order_summary',
              attributes: {
                country_code: alpha3Code,
                order_state: OrderState.PayRemainingBalance,
                selected_items_ids: selectedItemsIds,
                ...(orderSummary
                  ? {
                      use_site_credit: orderSummary.attributes.useSiteCredit,
                      use_mo_rewards: orderSummary.attributes.useMoRewards
                    }
                  : {})
              }
            }
          }
        }
      }),
    [alpha3Code, mutate, orderId, orderSummary]
  );

  return {
    updateOrderSelectedItems,
    setOrderStateToDefault,
    setOrderStateToPaying,
    setOrderPayRemainingBalance,
    loading,
    error
  };
};
